import CachedIcon from "@mui/icons-material/Cached";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import prettyBytes from "pretty-bytes";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { getAccountEmails } from "../../../api/account";
import { sendEmail } from "../../../api/email/sendEmail";
import { deleteFile } from "../../../api/file/deleteFile";
import { downloadFile } from "../../../api/file/downloadFile";
import { getStageFiles } from "../../../api/file/getStageFiles";
import { getProcedure, listTenders } from "../../../api/procedure";
import { getStageRole } from "../../../api/stage";
import { saveTerm } from "../../../api/stage/terms/saveTerm";
import { useTranslationHook } from "../../../hooks";
import { setLoading } from "../../../store/UI";
import { ProjectFile } from "../../../types/model/file";
import { Procedure, ProcedureRole } from "../../../types/model/procedure";
import {
  Stage,
  StageRole,
  Term,
  TermRequest,
  TermRequestSchema,
} from "../../../types/model/stage";
import { formatDate } from "../../../utils";
import DescriptionEditor from "../../DescriptionEditor/DescriptionEditor";
import ConfirmationDialog from "../../Generic/ConfirmationDialog";
import EmailRecipientSelector from "../../Generic/EmailRecipientSelector";
import InputDialog from "../../Generic/InputDialog";
import { StageFileUpload } from "../StageFileUpload";

interface TenderAccountEmails {
  accountId: number;
  accountName: string;
  emails: string[];
}

interface TermsProps {
  stage: Stage;
  term: Term | undefined;
  procedureRole: ProcedureRole;
  accountId: number;
  projectId: number;
  activityId: number;
  procedureId: number;
  procedure: Procedure;
}

const Terms = (props: TermsProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const {
    stage,
    term,
    procedureRole,
    accountId,
    projectId,
    activityId,
    procedureId,
    procedure,
  } = props;
  const [stageRole, setStageRole] = useState<StageRole>();
  const [uploadedFiles, setUploadedFiles] = useState<ProjectFile[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredFiles, setFilteredFiles] = useState<ProjectFile[]>([]);
  const [richDescription, setRichDescription] = useState("");

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isInputDialogOpen, setIsInputDialogOpen] = useState(false);
  const [fileForEmail, setFileForEmail] = useState<File | null>(null);
  const [currentProcedure, setCurrentProcedure] = useState<Procedure | null>(null);
  const [selectedRecipientEmails, setSelectedRecipientEmails] = useState<string[]>([]);
  const [tenderAccountEmails, setTenderAccountEmails] = useState<TenderAccountEmails[]>([]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const fetchStageRole = async () => {
    try {
      const data = await getStageRole(projectId, activityId, procedureId, stage.id);
      setStageRole(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = async (fileId: number, filename: string) => {
    dispatch(setLoading(true));
    try {
      await downloadFile(fileId, filename);
    } catch (error) {
      console.error("Download error:", error);
    }
    dispatch(setLoading(false));
  };

  const handleDeleteExistingFile = async (fileId: number) => {
    try {
      await deleteFile(fileId);
      fetchExistingFiles();
    } catch (error) {
      console.error("Deleting error", error);
    }
  };

  const handleCancelAction = () => {
    setEditMode(false);
  };

  const handleEditAction = () => {
    setEditMode(true);
  };

  const handleOnFileUpdated = () => {
    fetchExistingFiles();
  };

  const handleFileUploadAndNotify = async (uploadedFiles: File[]) => {
    fetchExistingFiles();
    if (uploadedFiles.length === 0) return;

    const procedure = await getProcedure(projectId, activityId, procedureId);
    setCurrentProcedure(procedure);

    if (procedure.started) {
      const tenders = await listTenders(projectId, activityId, procedureId);
      const tenderEmailsData: TenderAccountEmails[] = await Promise.all(
        tenders.map(async (tender) => {
          const emails = await getAccountEmails(tender.id);
          return {
            accountId: tender.id,
            accountName: tender.name,
            emails: emails,
          };
        })
      );
      setTenderAccountEmails(tenderEmailsData);
      setFileForEmail(uploadedFiles[0]);
      setIsConfirmDialogOpen(true);
    }
  };

  const sendNotifications = async (selectedRecipients: string[], customMessage: string, file: File | null) => {
    if (!currentProcedure) return;
    dispatch(setLoading(true));
    try {
      for (const tenderAccount of tenderAccountEmails) {
        const recipientsForThisAccount = tenderAccount.emails.filter((email) =>
          selectedRecipients.includes(email)
        );

        if (recipientsForThisAccount.length > 0) {
          try {
            const emailVariables: any = {
              procedureName: currentProcedure.name,
              accountName: tenderAccount.accountName,
              customMessage: customMessage || "",
            };

            if (currentProcedure.endDate) {
              emailVariables.endDate = new Intl.DateTimeFormat("pt-PT").format(new Date(currentProcedure.endDate));
            }

          if (currentProcedure.endDate) {
            emailVariables.endDate = new Intl.DateTimeFormat("pt-PT").format(new Date(currentProcedure.endDate));
          }
          if (currentProcedure.contactManagerId) {
            emailVariables.contactManagerId = currentProcedure.contactManagerId;
          }

          await sendEmail(
            {
              to: recipientsForThisAccount,
              cc: [],
              template: "atualizacoes",
              variables: {
                ...emailVariables,
              },  
            },
              file
            );
          } catch (error) {
            console.error(error);
          }
        }
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchExistingFiles = async () => {
    getStageFiles(stage.id)
      .then((response) => {
        setUploadedFiles(response);
        setFilteredFiles(response);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleSaveAction = (values: any) => {
    try {
      let newQuestion: TermRequest = TermRequestSchema.parse({
        terms: values.field1,
      });
      saveTerm(projectId, activityId, procedureId, stage.id, newQuestion)
        .then((response) => {
          setRichDescription(response.terms ? response.terms : "");
          enqueueSnackbar(t("stages.terms.saved"), {
            variant: "success",
          });
          setEditMode(false);
        })
        .catch((error) => {
          enqueueSnackbar(t("stages.terms.error-save"), { variant: "error" });
        });
    } catch (err) {
      enqueueSnackbar("stages.terms.error-save", { variant: "error" });
    }
  };

  useEffect(() => {
    fetchExistingFiles();
    setRichDescription(term ? term.terms : '');
    fetchStageRole();
  }, []);

  useEffect(() => {
    const lower = search.toLowerCase();
    const filtered = uploadedFiles.filter((file) =>
      file.originalName ? file.originalName.toLowerCase().includes(lower) : false
    );
    setFilteredFiles(filtered);
    setPage(0);
  }, [search, uploadedFiles]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("generic.stage") + " - " + t(stage.stageTypeLabel)}
      </Typography>

      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label={t("generic.details")} />
        <Tab label={t("generic.files")} />
      </Tabs>

      {tabIndex === 0 && (
        <Box>
          <Formik
            enableReinitialize
            initialValues={{
              field1: richDescription || undefined,
            }}
            onSubmit={(values) => {
              handleSaveAction(values);
            }}
            validationSchema={Yup.object().shape({
              field1: Yup.string(),
            })}
          >
            {(props) => {
              const {
                handleSubmit,
              } = props;
              return (
                <form onSubmit={handleSubmit}>
                  <Grid2 container spacing={3}>
                    {procedureRole.isManager &&
                      !procedure.canceled &&
                      !procedure.closed && (
                        <Grid2 size={12} justifyItems={"right"}>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              justifyContent: "flex-end",
                              alignItems: "center",
                            }}
                          >
                            {!editMode && (
                              <Tooltip title={t("generic.edit")}>
                                <IconButton onClick={handleEditAction}>
                                  <EditIcon sx={{ fontSize: 28 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {editMode && (
                              <>
                                <Tooltip title={t("generic.cancel")}>
                                  <IconButton
                                    onClick={handleCancelAction}
                                    sx={{ color: "red" }}
                                  >
                                    <CloseIcon sx={{ fontSize: 28 }} />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title={t("generic.save")}>
                                  <IconButton
                                    type="submit"
                                    sx={{ color: "green" }}
                                  >
                                    <SaveIcon sx={{ fontSize: 28 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        </Grid2>
                      )}
                    <Grid2 size={12}>
                      {/*Description*/}
                      <Box
                        sx={{
                          mb: 2,
                          "& .ProseMirror": {
                            height: "30dvh",
                            overflowY: "auto",
                          },
                        }}
                      >
                        <DescriptionEditor
                          initialText={richDescription}
                          isTender={procedureRole.isTender}
                          isEditMode={editMode}
                          onChange={(newValue) => {
                            props.setFieldValue("field1", newValue);
                          }}
                        />
                      </Box>
                    </Grid2>
                  </Grid2>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {tabIndex === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                label={t("generic.search") ?? "Search"}
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mr: 2 }}
              />
              <Tooltip title={t("generic.refresh")}>
                <IconButton onClick={fetchExistingFiles}>
                  <CachedIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Tabela de arquivos */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("generic.file-name") ?? "File name"}
                    </TableCell>
                    <TableCell>{t("generic.created")}</TableCell>
                    <TableCell>{t("generic.account")}</TableCell>
                    <TableCell>{t("generic.size")}</TableCell>
                    <TableCell align="right">
                      {t("generic.actions") ?? "Actions"}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFiles
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((file) => (
                      <TableRow key={file.id}>
                        <TableCell>{file.originalName}</TableCell>
                        <TableCell>
                          {file.createdOn
                            ? formatDate(file.createdOn.toString())
                            : ""}
                        </TableCell>
                        <TableCell>{file.accountName}</TableCell>
                        <TableCell>{prettyBytes(file.size)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() =>
                              handleDownload(
                                file.id,
                                file.originalName ? file.originalName : ""
                              )
                            }
                            color="primary"
                            size="small"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          {stageRole!.isManager &&
                            !procedure.canceled &&
                            !procedure.closed && (
                              <IconButton
                                onClick={() =>
                                  handleDeleteExistingFile(file.id)
                                }
                                color="error"
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginação */}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={filteredFiles.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number.parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>

          {stageRole?.isManager && !procedure.canceled && !procedure.closed && (
            <StageFileUpload
              accountId={accountId}
              projectId={projectId}
              activityId={activityId}
              procedureId={procedureId}
              stageId={stage.id}
              onFilesUpdated={handleFileUploadAndNotify}
              procedure={procedure}
            />
          )}
        </>
      )}

      <ConfirmationDialog
        open={isConfirmDialogOpen}
        title={t("tender.send-notification-title")}
        message={t("procedures.send-update-notification-message")}
        onConfirm={() => {
          setIsConfirmDialogOpen(false);
          setIsInputDialogOpen(true);
        }}
        onCancel={() => {
          setIsConfirmDialogOpen(false);
        }}
      />

      <InputDialog
        open={isInputDialogOpen}
        title={t("procedures.add-custom-message-title")}
        defaultFile={fileForEmail!}
        onConfirm={async (customMessage, file) => {
          await sendNotifications(selectedRecipientEmails, customMessage, file);
          setIsInputDialogOpen(false);
        }}
        onCancel={() => {
          sendNotifications(selectedRecipientEmails, "", fileForEmail);
          setIsInputDialogOpen(false);
        }}
      >
        <EmailRecipientSelector
          tenderAccountEmails={tenderAccountEmails}
          value={selectedRecipientEmails}
          onSelectionChange={(emails) => setSelectedRecipientEmails(emails)}
        />
      </InputDialog>
    </Box>
  );
};

export default Terms;
